<template>
  <div>
    <ValidationObserver ref="VerificationFormValidator">
      <form
        @submit.prevent="submit"
        data-qa="por-verification--form">
        <Input
          id="verification-code"
          validation-rules="required|numeric|code"
          :input-model.sync="code"
          :label-text="$t('emailVerifiationForm_verificationCode_label')"
          input-type="tel"
          :error-text="`${$t('emailVerificationForm_invalidCode_text')}`"
          data-qa="por-verification--code-input" />
        <b-button
          size="is-medium"
          type="is-primary"
          expanded
          :disabled="isSubmitting"
          :loading="isSubmitting"
          tag="input"
          native-type="submit"
          class="por-verification--top-spacing"
          :value="$t('emailVerification_registrationComplete_button')"
          data-qa="por-verification--button" />

        <div data-qa="por-verification--code-reminder">
          <div class="por-verification--top-spacing por-verification--form--center-alignment">
            <span>{{ $t('common_emailNotReceived_text') }}</span>
          </div>
          <div class="por-verification--form--center-alignment">
            <b-button
              tag="input"
              type="is-text"
              class="is-fullwidth por-standard-top-spacing"
              data-qa="por-verification--form-code-reminder-button"
              @click="resendCodeClick"
              :value="$t('emailVerificationForm_resendEmail_button')" />

          </div>
        </div>

        <hr class="por-verification--top-gap-spacing"/>

        <div data-qa="por-verification--redirect-to-registration">
          <div class="por-verification--top-spacing por-verification--form--center-alignment">
            <span>{{ $t('emailVerificationForm_wrongEmail_text') }}</span>
          </div>
          <div class="por-verification--form--center-alignment">
            <b-button
              type="is-text"
              tag="input"
              @click="redirectToRegister"
              :value="$t('emailVerificationForm_newAccount_text')"
              data-qa="por-verification--form-code-redirect-button" />
          </div>
        </div>

      </form>
    </ValidationObserver>
  </div>

</template>

<script>
import { Auth } from '@aws-amplify/auth'
import { mapGetters } from 'vuex'

import Input from '@/components/inputs/input'
import router from '@/router'
import trackErrors from '@/helpers/track-errors'
import { track, identify } from '@/services/tracking'
import { redirect } from '@/services/redirect'
import { isUnauthorizedUser } from '@/helpers/error-mapping'

export default {
  name: 'EmailVerificationForm',
  data () {
    return {
      code: '',
      submitStatus: null
    }
  },
  components: {
    Input
  },
  computed: {
    ...mapGetters({
      user: 'user/userCredentials'
    }),
    isSubmitting: function () {
      return this.submitStatus === 'PENDING'
    }
  },
  methods: {
    async submit () {
      track({ name: 'button tap', params: { module: 'email verification' } })
      this.submitStatus = 'PENDING'
      const success = await this.$refs.VerificationFormValidator.validate()

      if (!success) {
        this.submitStatus = 'ERROR'
        trackErrors(this.$refs.VerificationFormValidator, 'email verification')
        return
      }

      try {
        await Auth.confirmSignUp(this.user.email, this.code)
        track({ name: 'email verification success', params: { module: 'email verification' } })

        if (this.user.password && this.user.password !== '') {
          await this.completeSignIn()
        } else {
          await this.completePasswordReset()
        }
        this.submitStatus = 'SUBMITTED'
      } catch (error) {
        if (isUnauthorizedUser(error)) {
          // Email can be verified but password needs to be reset first
          router.push({ name: 'login' })
        }
        this.$store.dispatch('site/setMessage', { message: error.message, type: 'danger' })
        track({ name: 'error', params: { module: 'email verification', error_message: error.message } })
        this.submitStatus = null
      }
    },
    async completeSignIn () {
      await Auth.signIn(this.user.email, this.user.password)
      let newUser = await Auth.currentSession()
      let patientId = newUser.getIdToken().decodePayload().patient_id
      if (this.user.gender) {
        let identifyPayload = {
          firstName: this.user.given_name,
          lastName: this.user.family_name,
          email: this.user.email,
          gender: this.user.gender === 'm' ? 'male' : 'female'
        }
        identify(patientId, identifyPayload)
      } else {
        identify(patientId)
      }

      track({ name: 'login success', params: { module: 'email verification' } })
      this.submitStatus = null
      redirect()
    },
    async completePasswordReset () {
      await Auth.forgotPassword(this.user.email)
      track({ name: 'forgot password success', params: { module: 'forgot_password' } })
      router.push({ name: 'request-password' })
      const successMessage = {
        message: 'You have successfully verified your email address. Now, you can reset your password. Please check your inbox for an email with a code to complete the process.',
        type: 'success'
      }
      this.$store.dispatch('site/setMessage', successMessage)
    },
    async resendCodeClick () {
      track({ name: 'button tap', params: { module: 'email verification', description: 're-send email verification email' } })

      try {
        await Auth.resendSignUp(this.user.email)

        this.$store.dispatch('site/setMessage', {
          message: 'common_verificationCode_text',
          type: 'success'
        })
        // requested to scroll to top for success notification
        window.scrollTo(0, 0)
      } catch (error) {
        this.$store.dispatch('site/setMessage', { message: error.message, type: 'danger' })
        track({ name: 'error', params: { module: 'email verification', description: 're-send email verification email', error_message: error.message } })
        this.submitStatus = null
      }
    },
    redirectToRegister () {
      track({ name: 'button tap', params: { module: 'email verification', description: 'create new account' } })
      this.$store.dispatch('site/setMessage', { message: null, type: null })
      router.push({ name: 'register' })
    }
  },
  created () {
    if (!this.user.email) {
      this.$store.dispatch('site/setMessage', { message: 'Please login.', type: 'danger' })
      router.push({ name: 'login' })
    }
  }
}
</script>

<style lang="scss">
  $por-standard-spacing: 16px;
  $por-verification--top-spacing: 88px;
  $button-focus-box-shadow-size: none; // overriding Bulma variable

  .por-verification--top-gap-spacing {
    margin-top: $por-verification--top-spacing;
  }

  // TODO: Review button with the shared components
  .button.por-text-button.is-medium {
    font-size: $size-6;
    font-weight: $weight-semibold;
    color: $primary;
    text-decoration: none;
    &:focus {
      outline: none;
      box-shadow: none;
    }
  }

  .por-verification--top-spacing {
    margin-top: $por-standard-spacing
  }
  .por-verification--form--center-alignment {
    text-align: center;
  }
</style>
